import { useMemo, useRef, useEffect, useState } from "react"
import type { DebounceSettings } from "lodash"
import debounce from "lodash/debounce"

type Cb = (...args: any[]) => any

/**
 * Custom hook to create a debounced function that can access the latest callback.
 * This hook ensures that the debounced function always invokes the latest version
 * of the provided callback, even if the callback changes after the debounce is set up.
 * This is useful for debouncing functions that depend on changing state or props.
 *
 * @param cb - The callback function to debounce. This callback can include any logic and will
 *             capture the most recent values of variables it depends on, due to being wrapped
 *             in a ref that is updated on every render.
 * @param t - The debounce timeout in milliseconds. This is the delay before the debounced
 *            function is allowed to invoke the callback after the last call to it. Defaults to 350ms.
 * @param options - Optional debounce settings provided to lodash's debounce function. This can
 *                  include settings like `leading` and `trailing` to control the behavior of the
 *                  debounced function.
 *
 * @returns A debounced function that is stable across re-renders and updates to invoke the latest
 *          callback. Use this returned function as you would use the original function, but with
 *          debouncing applied.
 */
export function useDebounceWithReferences(
  cb: Cb,
  t = 350,
  options?: DebounceSettings,
) {
  const callbackRef = useRef(cb)

  // Always keep the callback ref up to date (safe to mutate during render)
  callbackRef.current = cb

  // Create the debounced function using useMemo to ensure it's created synchronously
  // and only recreated when t or options change
  const debouncedFn = useMemo(() => {
    return debounce(
      (...args: any[]) => {
        // Call the latest callback
        callbackRef.current(...args)
      },
      t,
      options,
    )
  }, [t, options])

  // Cleanup on unmount or when debounced function changes
  useEffect(() => {
    return () => {
      debouncedFn.cancel()
    }
  }, [debouncedFn])

  return debouncedFn
}

export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timeout)
    }
  }, [value, delay])

  return debouncedValue
}
